'use client';

import { capturePostHogClientException } from '@/lib/analytics/posthog-client-errors';
import type { PostHogErrorProperties } from '@/lib/analytics/posthog-errors';
import { markErrorReported, wasErrorReported } from './report-once';
import { isReportableClientError } from './reportable';

export type ClientErrorReport = {
  /** Where the error was caught, e.g. `toast`, `swr`, `clog`. */
  source: string;
  /** Extra safe properties. Avoid redacted names such as `message` or `body`. */
  properties?: PostHogErrorProperties;
};

/**
 * Sends a browser-side `$exception` to PostHog, which alerts Slack. The user
 * is attached by posthog-js. Skips expected errors, the browser's own network
 * failures, our API's 5xx answers (the server reported those) and errors
 * already reported, so a catch that both logs and toasts the same error
 * reports once.
 */
export function reportClientError(
  error: unknown,
  { source, properties }: ClientErrorReport,
): void {
  if (!isReportableClientError(error) || wasErrorReported(error)) return;

  capturePostHogClientException(error, { errorSource: source, properties });
  markErrorReported(error);
}
